import { supabase } from '../utils/supabase';

/**
 * Service for managing VA activities
 */
const activityService = {
  /**
   * Fetches recent activities for a VA
   * @param {string} vaId - The VA's user ID
   * @param {number} limit - Maximum number of activities to fetch
   * @returns {Promise<Object>} - Object with success status and data
   */
  getRecentActivities: async (vaId, limit = 10) => {
    if (!vaId) {
      return { success: false, error: 'VA ID is required' };
    }

    try {
      // Query the va_activities table
      const { data, error } = await supabase
        .from('va_activities')
        .select('*')
        .eq('va_id', vaId)
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Map to the expected format
      const activities = data.map(activity => ({
        id: activity.id,
        type: activity.type,
        title: activity.title,
        description: activity.description,
        timestamp: new Date(activity.timestamp),
        metadata: activity.metadata || {}
      }));

      return { success: true, data: activities };
    } catch (error) {
      console.error('Error in getRecentActivities:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Creates a new activity record
   * @param {Object} activityData - Activity data
   * @returns {Promise<Object>} - Object with success status and data
   */
  createActivity: async (activityData) => {
    const { vaId, type, title, description, metadata } = activityData;
    
    if (!vaId || !type || !title || !description) {
      return { success: false, error: 'Missing required fields' };
    }

    try {
      // Get the current authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Check if the current user matches the provided vaId or is an admin
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user?.id)
        .single();
        
      if (profileError) throw profileError;
      
      // Only allow if the user is creating an activity for themselves or is an admin
      if (!user || (user.id !== vaId && userProfile?.role !== 'admin')) {
        return { 
          success: false, 
          error: 'Cannot create activities for another user due to security policies'
        };
      }

      const { data, error } = await supabase
        .from('va_activities')
        .insert([{
          va_id: vaId,
          type,
          title,
          description,
          metadata
        }])
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error in createActivity:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Checks if a table exists in the database
   * @param {string} tableName - Name of the table to check
   * @returns {Promise<boolean>} - Whether the table exists
   */
  checkTableExists: async (tableName) => {
    try {
      // Use a SELECT query that will fail if the table doesn't exist
      const { error } = await supabase
        .from(tableName)
        .select('id')
        .limit(1);
      
      if (error) {
        if (error.code === '42P01') { // Table doesn't exist
          return false;
        }
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error(`Error checking if table ${tableName} exists:`, error);
      return false;
    }
  },

  // The populateSampleActivities method has been removed as we no longer want to generate mock data
};

export default activityService;
